import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import type { ColumnSortDirection } from '../../hooks/useColumnSort';

/**
 * CR-7263 — a table header that sorts, and looks like it does.
 *
 * The cursor, hover and caret are the PO Lines list's own affordance
 * (`POLinesTable` + `getSortIcon`), lifted here so the tables in the PO Line
 * pop-up match the screens above them. A header that sorts but gives no sign
 * of it is the same complaint in a different form, so the caret is not
 * optional: an unsorted column shows the neutral double-arrow, the active one
 * shows its direction.
 *
 * The click target is a real `<button>` inside the `<th>`, not a handler on
 * the `<th>` itself. That is the only arrangement that gives both halves:
 * `aria-sort` is meaningful only on an element that is still a
 * `columnheader`, so the cell has to keep its role, while a keyboard user
 * needs something focusable that answers Enter and Space. Putting
 * `role="button"` on the header buys the second by destroying the first — it
 * announces a sort state on a cell that no longer reads as a column header.
 * The button is stretched to fill the cell, and carries the padding the cell
 * used to have, so clicking anywhere in the header still sorts.
 */
interface SortableThProps {
  /** The sort field this column maps to. */
  field: string;
  /** Currently sorted field, from useColumnSort. */
  sortField: string | null;
  sortDirection: ColumnSortDirection;
  onSort: (field: string) => void;
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  /**
   * Cell classes. Padding here is applied to the inner button instead, so the
   * whole header stays clickable rather than only the words in the middle.
   */
  className?: string;
  /** CR-8667: passed to the cell, for two-level headers and frozen columns. */
  rowSpan?: number;
  style?: React.CSSProperties;
  title?: string;
  /** Hover fill. A sticky header needs a solid one, or scrolled rows show through. */
  hoverClassName?: string;
}

function sortIcon(field: string, sortField: string | null, sortDirection: ColumnSortDirection) {
  if (sortField !== field) {
    return <ArrowUpDown className="h-3 w-3 text-muted-foreground" />;
  }
  return sortDirection === 'asc'
    ? <ArrowUp className="h-3 w-3 text-primary" />
    : <ArrowDown className="h-3 w-3 text-primary" />;
}

export function SortableTh({
  field, sortField, sortDirection, onSort, children, align = 'left', className = '',
  rowSpan, style, title, hoverClassName = 'hover:bg-muted/50',
}: SortableThProps) {
  const alignClass = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';
  const justifyClass = align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : '';
  const active = sortField === field;
  // Padding moves from the cell to the button, so the caller's spacing is
  // unchanged but the whole header is clickable rather than only its text.
  const classes = className.split(/\s+/).filter(Boolean);
  const isPadding = (c: string) => /^-?p[xytblr]?-/.test(c);
  const cellClasses = classes.filter((c) => !isPadding(c)).join(' ');
  const padClasses = classes.filter(isPadding).join(' ') || 'px-4 py-3';
  return (
    <th
      className={`${alignClass} p-0 ${hoverClassName} select-none ${cellClasses}`}
      aria-sort={active ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
      rowSpan={rowSpan}
      style={style}
      title={title}
    >
      <button
        type="button"
        onClick={() => onSort(field)}
        className={`w-full cursor-pointer bg-transparent border-0 ${alignClass} ${padClasses}
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset`}
      >
        <span className={`flex items-center gap-1 ${justifyClass}`}>
          {children}
          {sortIcon(field, sortField, sortDirection)}
        </span>
      </button>
    </th>
  );
}
